import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  TextField,
  Button,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import DeleteConfirmation from "../../common/DeleteConfirmation"; // Import the DeleteConfirmation component
import { DeleteProcess } from "../../../services/TestProcessService";
import { NotificationManager } from "react-notifications";
import { useNavigate } from "react-router-dom";

const TestProcessTable = ({ clientsData, count, onChange }) => {
  const columns = [
    "Process Id",
    "Description",
    "Record Id",
    "Process Type",
    "Actions",
  ];
  const columnHeaders = ["processId", "description", "recordid", "processtype"];

  // Process type mapping for display
  const processTypeLabels = {
    0: "Validation",
    1: "Billing",
    2: "Import",
    3: "Reporting",
    4: "Other",
  };

  const rowsPerPageOptions = [10, 25, 50, 100, 200];

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false); // State for delete confirmation
  const [selectedItemToDelete, setSelectedItemToDelete] = useState(null); // State to store the selected item to delete
  const navigate = useNavigate();

  useEffect(() => {
    setSearchTerm("");
    handleChange();
  }, [page, rowsPerPage]);

  // Updated filtering logic to include process type labels in search
  useEffect(() => {
    const filteredResults = clientsData.filter((client) =>
      columnHeaders.some((header) => {
        const value = client[header];

        // Special handling for processtype to search by both numeric value and label
        if (header === "processtype") {
          const numericValue = value != null ? String(value) : "";
          const labelValue = processTypeLabels[value] || "";
          return (
            numericValue.toLowerCase().includes(searchTerm.toLowerCase()) ||
            labelValue.toLowerCase().includes(searchTerm.toLowerCase())
          );
        }

        // Check if value exists and is not null/undefined, then convert to string and search
        return (
          value != null &&
          String(value).toLowerCase().includes(searchTerm.toLowerCase())
        );
      })
    );

    setFilteredData(filteredResults);
    count = filteredData.length;
  }, [searchTerm, clientsData]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChange = () => {
    onChange(page, rowsPerPage);
  };

  const handleViewClick = (client) => {
    setSelectedClient(client);
  };

  const handleAddNewClick = () => {
    navigate("/vlp/test-process-configuration/add");
  };

  const handleEditClick = (process) => {
    navigate("/vlp/test-process-configuration/" + process.processId);
  };

  const handleDeleteClick = (client) => {
    setSelectedItemToDelete(client);
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false);
  };

  const handleDeleteConfirmationConfirm = async () => {
    try {
      const response = await DeleteProcess(selectedItemToDelete.processId);
      if (response.results === true) {
        NotificationManager.success("Process deleted successfully");
        setIsDeleteConfirmationOpen(false);
      } else {
        NotificationManager.error("Error deleting Process");
        setIsDeleteConfirmationOpen(false);
      }
    } catch (error) {
      NotificationManager.error("Error deleting Process");
      setIsDeleteConfirmationOpen(false);
    }
  };

  // Function to render cell content with special handling for process type
  const renderCellContent = (client, column) => {
    if (column === "processtype") {
      const processTypeValue = client[column];
      return processTypeLabels[processTypeValue] || processTypeValue || "-";
    }
    return client[column] || "-";
  };

  const renderActions = (client) => (
    <div style={{ minWidth: "120px" }}>
      <IconButton
        aria-label="edit"
        style={{ color: "orange" }}
        onClick={() => handleEditClick(client)}
      >
        <EditIcon />
      </IconButton>
      <IconButton
        aria-label="view"
        style={{ color: "green" }}
        onClick={() => handleViewClick(client)}
      >
        <VisibilityIcon />
      </IconButton>
      <IconButton
        aria-label="delete"
        style={{ color: "red" }}
        onClick={() => handleDeleteClick(client)}
      >
        <DeleteIcon />
      </IconButton>
    </div>
  );

  return (
    <>
      <Paper style={{ marginRight: "20px" }}>
        <div
          style={{
            display: "flex",
            marginBottom: "16px",
            alignItems: "center",
          }}
        >
          <TextField
            label="Search"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ marginRight: "16px", width: "80%" }}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddNewClick}
          >
            New
          </Button>
        </div>
        <TableContainer>
          <Table sx={{ minWidth: 400 }} size="small" dense>
            <TableHead
              sx={{
                backgroundColor: (theme) => theme.palette.primary.main,
                color: "white",
              }}
            >
              <TableRow>
                {columns.map((column) => (
                  <TableCell key={column}>{column}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData
                // .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((client, index) => (
                  <TableRow
                    key={client.accountNumber}
                    sx={{
                      backgroundColor:
                        index % 2 === 0 ? "rgba(0, 0, 0, 0.04)" : "white",
                    }}
                  >
                    {columnHeaders.map((column) => (
                      <TableCell key={column}>
                        {renderCellContent(client, column)}
                      </TableCell>
                    ))}
                    <TableCell>{renderActions(client)}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={count}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Render the delete confirmation popup */}
      <DeleteConfirmation
        isOpen={isDeleteConfirmationOpen}
        onClose={handleDeleteConfirmationClose}
        onConfirm={handleDeleteConfirmationConfirm}
        title="Delete Confirmation"
        message={`Are you sure you want to delete the ${selectedItemToDelete?.processId} process?`}
        color="red"
      />
    </>
  );
};

export default TestProcessTable;
